import { clsx } from 'clsx';
import { Filter as FilterIcon } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import type { FilterProps } from './types';

const Filter = ({ column, onFilterChange }: FilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const columnKey = column.key;

    // Handle outside clicks to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleFilterClick = (value: string) => {
        onFilterChange(columnKey, value);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            toggleDropdown();
        }
    };

    return (
        <div className="relative flex w-full items-center justify-between" ref={dropdownRef}>
            <button
                className="group inline-flex w-full items-center justify-between"
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                aria-label="Filter"
                aria-expanded={isOpen}
                tabIndex={0}
            >
                <span className="w-full text-left">{column.title}</span>
                <span className="ml-3 flex-none rounded text-gray-500 group-hover:visible group-focus:visible">
                    <FilterIcon className="size-5" />
                </span>
            </button>

            {isOpen && column.filterOptions && (
                <div
                    className="absolute top-full right-0 z-10 mt-1 min-w-[100px] rounded-md border border-gray-200 bg-white shadow-lg"
                    role="menu"
                >
                    {column.filterOptions.map(option => (
                        <button
                            key={option.value}
                            className={clsx('w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-gray-100')}
                            onClick={() => handleFilterClick(option.value)}
                            role="menuitem"
                            tabIndex={0}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Filter;
